import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { BriefcaseIcon as Briefcase, PlusIcon as Plus, TrashIcon as Trash, CaretUpIcon as CaretUp, CaretDownIcon as CaretDown, WrenchIcon as Wrench, FlagIcon as Flag, PackageIcon as Package } from "@phosphor-icons/react";

const clone = (x) => JSON.parse(JSON.stringify(x));

// Lista di stringhe semplice (skill, stati merce)
function StringList({ items, onChange, placeholder, testid }) {
  const upd = (i, v) => { const n = [...items]; n[i] = v; onChange(n); };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const n = [...items]; [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((it, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`${testid}-row-${i}`}>
          <div className="flex flex-col shrink-0" title="Sposta">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="h-4 w-5 flex items-center justify-center text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-default" data-testid={`${testid}-up-${i}`}><CaretUp size={12} weight="bold" /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="h-4 w-5 flex items-center justify-center text-muted-foreground hover:text-primary disabled:opacity-30 disabled:cursor-default" data-testid={`${testid}-down-${i}`}><CaretDown size={12} weight="bold" /></button>
          </div>
          <Input value={it} onChange={(e) => upd(i, e.target.value)} placeholder={placeholder} className="rounded-sm" data-testid={`${testid}-input-${i}`} />
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}><Trash size={14} /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="rounded-sm gap-1 mt-1" onClick={() => onChange([...items, ""])} data-testid={`${testid}-add`}><Plus size={14} /> Aggiungi</Button>
    </div>
  );
}

// Lista fasi: label + colore + attivo + riordino
function FasiList({ items, onChange, testid }) {
  const upd = (i, k, v) => { const n = clone(items); n[i][k] = v; onChange(n); };
  const move = (i, dir) => { const j = i + dir; if (j < 0 || j >= items.length) return; const n = clone(items); [n[i], n[j]] = [n[j], n[i]]; onChange(n); };
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map((f, i) => (
        <div key={i} className="flex items-center gap-2" data-testid={`${testid}-row-${i}`}>
          <div className="flex flex-col">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="text-muted-foreground disabled:opacity-30 hover:text-foreground" data-testid={`${testid}-up-${i}`}><CaretUp size={12} /></button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-muted-foreground disabled:opacity-30 hover:text-foreground" data-testid={`${testid}-down-${i}`}><CaretDown size={12} /></button>
          </div>
          <input type="color" value={f.color || "#94a3b8"} onChange={(e) => upd(i, "color", e.target.value)} className="h-9 w-10 rounded-sm border border-border cursor-pointer shrink-0" data-testid={`${testid}-color-${i}`} title="Colore" />
          <Input value={f.label || ""} onChange={(e) => upd(i, "label", e.target.value)} placeholder="Nome fase" className="rounded-sm" data-testid={`${testid}-label-${i}`} />
          <div className="flex items-center shrink-0" title="Attiva"><Switch checked={f.attivo !== false} onCheckedChange={(v) => upd(i, "attivo", v)} data-testid={`${testid}-active-${i}`} /></div>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-destructive shrink-0" onClick={() => onChange(items.filter((_, j) => j !== i))} data-testid={`${testid}-remove-${i}`}><Trash size={14} /></Button>
        </div>
      ))}
      <Button variant="outline" size="sm" className="rounded-sm gap-1 mt-1" onClick={() => onChange([...items, { key: "", label: "", color: "#2563eb", attivo: true }])} data-testid={`${testid}-add`}><Plus size={14} /> Aggiungi fase</Button>
    </div>
  );
}

const Block = ({ icon: Icon, title, desc, children }) => (
  <div className="border-t border-border pt-5 mt-5 first:border-0 first:pt-0 first:mt-0">
    <div className="flex items-center gap-2 mb-3">
      <Icon size={16} className="text-primary" />
      <div><h4 className="font-heading font-semibold text-sm">{title}</h4>{desc && <p className="text-xs text-muted-foreground">{desc}</p>}</div>
    </div>
    {children}
  </div>
);

export function LavorazioniConfigSection() {
  const [cfg, setCfg] = useState(null);

  useEffect(() => { api.get("/lavorazioni/settings").then(({ data }) => setCfg(data)).catch(() => {}); }, []);

  const setPart = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const save = async () => {
    try {
      const payload = {
        skill: (cfg.skill || []).filter((s) => s.trim()),
        stati_merce: (cfg.stati_merce || []).filter((s) => s.trim()),
        fasi: (cfg.fasi || []).filter((f) => (f.label || "").trim()),
      };
      const { data } = await api.put("/lavorazioni/settings", payload);
      setCfg(data);
      toast.success("Configurazione Lavorazioni salvata");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Errore"); }
  };

  if (!cfg) return null;
  return (
    <Card className="rounded-sm border-border shadow-none p-6 mb-6" data-testid="lavorazioni-config-section">
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-sm bg-secondary flex items-center justify-center"><Briefcase size={20} weight="bold" className="text-primary" /></div>
          <div><h3 className="font-heading font-semibold">Lavorazioni</h3><p className="text-xs text-muted-foreground">Skill tecnici, fasi lavorazione e stati merce del modulo Lavorazioni</p></div>
        </div>
        <Button onClick={save} className="rounded-sm shrink-0" data-testid="save-lavorazioni-config-btn">Salva configurazione</Button>
      </div>

      <Block icon={Wrench} title="Skill tecnici" desc="Competenze selezionabili nelle righe tecnici e nelle skill richieste">
        <StringList items={cfg.skill || []} onChange={(v) => setPart("skill", v)} placeholder="Nome skill" testid="lav-skill" />
      </Block>
      <Block icon={Flag} title="Fasi lavorazione" desc="Colonne del kanban e stati della lavorazione (ordina con le frecce, colore configurabile)">
        <FasiList items={cfg.fasi || []} onChange={(v) => setPart("fasi", v)} testid="lav-fasi" />
      </Block>
      <Block icon={Package} title="Stati merce" desc="Stati disponibili per la disponibilità della merce">
        <StringList items={cfg.stati_merce || []} onChange={(v) => setPart("stati_merce", v)} placeholder="Stato merce" testid="lav-stati-merce" />
      </Block>

      <div className="flex justify-end mt-5">
        <Button onClick={save} className="rounded-sm" data-testid="save-lavorazioni-config-bottom-btn">Salva configurazione</Button>
      </div>
    </Card>
  );
}
